import React, { useEffect, useState } from 'react';
import { Toast, ToastContainer, Spinner } from 'react-bootstrap';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AppShell } from './components/shell';
import { BreadcrumbProvider } from './contexts/BreadcrumbContext';
import Login from './components/Login';
import PhenologyCapture from './components/PhenologyCapture';
import ReconciliationDashboard from './components/ReconciliationDashboard';
import OrphanRecoveryDashboard from './components/OrphanRecoveryDashboard';
import PhoneCheckPage from './components/phoneCheck/PhoneCheckPage';
import Settings from './components/Settings';
import UpdateToast from './components/UpdateToast';
import ChangePasswordRequired from './components/auth/ChangePasswordRequired';
import MustChangePasswordGate from './components/auth/MustChangePasswordGate';
import SystemLayout from './components/system/SystemLayout';
import SystemTenantsPage from './components/system/SystemTenantsPage';
import SystemUsersPage from './components/system/SystemUsersPage';
import SystemPestsPage from './components/system/SystemPestsPage';
import SystemIntegrityPage from './components/system/SystemIntegrityPage';
import SystemCropTypesPage from './components/system/SystemCropTypesPage';
import ProtectedRoute from './components/ProtectedRoute';
import { RequireMenu } from './auth/RequireMenu';
import { RequireLinkedOperator } from './auth/RequireLinkedOperator';
import { Home } from './components/Home';
import ScoutingLayout from './components/scouting/ScoutingLayout';
import { ScoutingDashboard } from './components/dashboard/ScoutingDashboard';
import PestConfigScreen from './components/scouting/PestConfigScreen';
import FieldScoutModePage from './components/scouting/recording/FieldScoutModePage';
import FieldScoutDesktopPage from './components/scouting/recording/desktop/FieldScoutDesktopPage';
import ReviewSummaryPage from './components/scouting/review/ReviewSummaryPage';
import ReviewMatrixPage from './components/scouting/review/ReviewMatrixPage';
import AuditLayout from './components/audit/AuditLayout';
import AuditLanding from './components/audit/AuditLanding';
import SprayLayout from './components/spray/SprayLayout';
import { SprayDashboard } from './components/dashboard/SprayDashboard';
import SprayProductsPage from './components/spray/SprayProductsPage';
import SprayProductDetailPage from './components/spray/SprayProductDetailPage';
import SprayStockLevelsPage from './components/spray/SprayStockLevelsPage';
import SprayStockMovementsPage from './components/spray/SprayStockMovementsPage';
import SpraySessionsPage from './components/spray/SpraySessionsPage';
import OperatorSessionList from './components/spray/OperatorSessionList';
import OperatorComboWorkFlow from './components/spray/OperatorComboWorkFlow';
import SpraySessionCompleteLanding from './components/spray/SpraySessionCompleteLanding';
import BackCaptureListPage from './components/spray/BackCaptureListPage';
import BackCaptureFormPage from './components/spray/BackCaptureFormPage';
import SprayProgramsPage from './components/spray/SprayProgramsPage';
import SprayMachinesPage from './components/spray/SprayMachinesPage';
import SprayOperatorsPage from './components/spray/SprayOperatorsPage';
import DataTransferPanel from './components/dataTransfer/DataTransferPanel';
import AuditReportDashboard from './components/audit/AuditReportDashboard';
import ConsumablesLayout from './components/consumables/ConsumablesLayout';
import ConsumableCatalogPage from './components/consumables/ConsumableCatalogPage';
import ConsumableCatalogDetailPage from './components/consumables/ConsumableCatalogDetailPage';
import ConsumableStockLevelsPage from './components/consumables/ConsumableStockLevelsPage';
import ConsumableStockMovementsPage from './components/consumables/ConsumableStockMovementsPage';
import UnitReviewPage from './components/consumables/UnitReviewPage';
import ConsumableProcurementListPage from './components/consumables/ConsumableProcurementListPage';
import ConsumableProcurementDetailPage from './components/consumables/ConsumableProcurementDetailPage';
import ConsumableProcurementFormPage from './components/consumables/ConsumableProcurementFormPage';
import ConsumableStockCountPage from './components/consumables/ConsumableStockCountPage';
import NutritionLayout from './components/nutrition/NutritionLayout';
import NutritionProductsPage from './components/nutrition/NutritionProductsPage';
import NutritionAuditPage from './components/nutrition/NutritionAuditPage';
import NutritionInventoryPage from './components/nutrition/NutritionInventoryPage';
import NutritionDashboardPage from './components/nutrition/NutritionDashboardPage';
import NutritionSessionDetailPage from './components/nutrition/NutritionSessionDetailPage';
import NutritionFoliarFieldPage from './components/nutrition/NutritionFoliarFieldPage';
import NutritionMySessionsPage from './components/nutrition/NutritionMySessionsPage';
import SoilWorkspaceLayout from './components/nutrition/SoilWorkspaceLayout';
import SoilBoardPage from './components/nutrition/SoilBoardPage';
import SoilProgramsListPage from './components/nutrition/SoilProgramsListPage';
import SoilProgramImportPage from './components/nutrition/SoilProgramImportPage';
import SoilProgramReviewPage from './components/nutrition/SoilProgramReviewPage';
import SoilGroupSetEditorPage from './components/nutrition/SoilGroupSetEditorPage';
import SoilGroupingPage from './components/nutrition/SoilGroupingPage';
import SoilSessionDetailPage from './components/nutrition/SoilSessionDetailPage';
import SoilInstructionPage from './components/nutrition/SoilInstructionPage';
import SoilMyGroupsPage from './components/nutrition/SoilMyGroupsPage';
import SoilGroupFieldPage from './components/nutrition/SoilGroupFieldPage';
import SoilSignOffPage from './components/nutrition/SoilSignOffPage';
import FoliarWorkspaceLayout from './components/nutrition/FoliarWorkspaceLayout';
import FoliarBoardPage from './components/nutrition/FoliarBoardPage';
import FoliarSignOffPage from './components/nutrition/FoliarSignOffPage';
import FoliarRoundPage from './components/nutrition/FoliarRoundPage';
import FoliarSetupPage from './components/nutrition/FoliarSetupPage';
import FoliarProgrammeEditorPage from './components/nutrition/FoliarProgrammeEditorPage';
import FoliarRoundEditorPage from './components/nutrition/FoliarRoundEditorPage';
import WeatherHistoryDashboard from './components/spray/WeatherHistoryDashboard';
import NotFoundPage from './components/NotFoundPage';
import ErrorBoundary from './components/ErrorBoundary';
import { usePwaUpdate } from './contexts/PwaUpdateContext';
import { useAuth } from './contexts/AuthContext';
import { useDatePreset } from './useDatePreset';
import { ModuleNavProvider } from './contexts/ModuleNavContext';
import { db } from './db';
import { syncPests } from './services/pestService';
import { syncOrchards } from './services/orchardSyncService';
import { initSyncEngine } from './services/syncService';
import { runCleanup } from './services/cleanupService';
import { runFieldScoutCleanup, FIELD_SCOUT_CLEANUP_DELAY_MS } from './services/fieldScoutCleanup';

const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;

/** /nutrition/soil-programs/:id/sessions → /nutrition/soil?program=:id (the
 *  programme must survive the redirect or the board silently falls back to the
 *  active one, which may not be the programme the link referred to). */
const LegacySoilSessionsRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/nutrition/soil?program=${id}`} replace />;
};

/** /nutrition/soil-application-sessions/:id → /nutrition/soil/sessions/:id (U3).
 *  SoilSessionGroupingPage, the page this route used to render, was removed
 *  in U5. */
const LegacySoilSessionDetailRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/nutrition/soil/sessions/${id}`} replace />;
};

/** /nutrition/soil-programs/:id/review → /nutrition/soil/programs/:id/review (U4). */
const LegacySoilReviewRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/nutrition/soil/programs/${id}/review`} replace />;
};

// Spec §3.2: /nutrition/sessions/:id → /nutrition/foliar/sessions/:id.
// F6 retired the bare /nutrition/sessions list (NutritionSessionsPage) and its
// manual New Session wizard — the last surface that produced this URL.
// Nothing in the app links here anymore: the printed instruction sheet's QR
// was retargeted to a constant worklist path (nutritionInstructionQr.js), and
// FoliarRoundPage's own session links were repointed separately (F3 Task 9).
// This per-id redirect stays as a regression guard against a stale bookmark
// or an old printed sheet still carrying the legacy URL.
const LegacyFoliarSessionDetailRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/nutrition/foliar/sessions/${id}`} replace />;
};

/** /nutrition/programs/:id/{edit,review} → /nutrition/foliar/setup/programmes/:id (F4).
 *  Edit and review collapsed into one editor — a bare <Navigate> cannot carry :id. */
const LegacyProgrammeEditorRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/nutrition/foliar/setup/programmes/${id}`} replace />;
};

function AppInner() {
  const { user, loading } = useAuth();
  // Re-render the whole app when the date-format preset changes so every
  // formatDate/formatTime/... call re-reads the new locale.
  useDatePreset();
  const [showStaleWarning, setShowStaleWarning] = useState(false);
  const { updateAvailable, updateApp } = usePwaUpdate();
  const [showUpdateToast, setShowUpdateToast] = useState(false);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);

  // Show toast immediately when a new update arrives (resets any prior dismissal)
  useEffect(() => {
    if (updateAvailable) {
      setShowUpdateToast(true);
      setDismissedAt(null);
    }
  }, [updateAvailable]);

  // Re-show toast after 4 hours if still dismissed
  useEffect(() => {
    if (!updateAvailable || dismissedAt === null) return;
    const elapsed = Date.now() - dismissedAt;
    const remaining = FOUR_HOURS_MS - elapsed;
    if (remaining <= 0) {
      setShowUpdateToast(true);
      return;
    }
    const timer = setTimeout(() => setShowUpdateToast(true), remaining);
    return () => clearTimeout(timer);
  }, [dismissedAt, updateAvailable]);

  const handleDismissToast = () => {
    setShowUpdateToast(false);
    setDismissedAt(Date.now());
  };

  useEffect(() => {
    // Initialize the database and ensure it opens on app load
    db.open()
      .then(async () => {
        console.log('IndexedDB opened successfully');
      })
      .catch((err) => {
        console.error('Initialization error:', err);
      });
  }, []);

  // Clear uploaded rounds, finished drafts and retired compact sessions a few
  // seconds after launch (never unsent work).
  useEffect(() => {
    const t = setTimeout(() => {
      runFieldScoutCleanup().catch(err => console.warn('[FieldScoutCleanup] failed:', err));
    }, FIELD_SCOUT_CLEANUP_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  // Synchronize data when user is logged in
  useEffect(() => {
    if (user) {
      // Synchronize pests and orchards for offline use
      syncPests().catch(err => console.error('Pest sync failed:', err));
      syncOrchards().catch(err => console.error('Orchard sync failed:', err));
      
      // Initialize the background sync engine
      initSyncEngine();
      // Run background cleanup
      runCleanup();

      // Check for stale orchard data
      const checkStale = async () => {
        const orchards = await db.orchards.toArray();
        if (orchards.length > 0) {
          const STALE_THRESHOLD_DAYS = 7;
          const timestamps = orchards
            .map(o => o.lastUpdated ? new Date(o.lastUpdated).getTime() : 0)
            .filter(t => t > 0);
          
          if (timestamps.length > 0) {
            const minTimestamp = Math.min(...timestamps);
            const daysOld = (new Date().getTime() - minTimestamp) / (1000 * 60 * 60 * 24);
            if (daysOld > STALE_THRESHOLD_DAYS) {
              setShowStaleWarning(true);
            }
          }
        }
      };
      checkStale();
    }
  }, [user]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Router>
      {!user ? (
        <div className="App">
          <Routes>
            <Route path="*" element={<Login />} />
          </Routes>
        </div>
      ) : (
      <div className="App">
        <Routes>
          {/* Standalone auth surface — no AppShell */}
          <Route path="/change-password" element={<ChangePasswordRequired />} />

          {/* Everything else: gate + AppShell + the existing nested routes */}
          <Route path="/*" element={
            <MustChangePasswordGate>
              <AppShell>
                <Routes>
                  <Route path="/" element={
                    user?.superadmin
                      ? <Navigate to="/system" replace />
                      : <ErrorBoundary moduleName="Home"><Home user={user} /></ErrorBoundary>
                  } />
                  <Route path="/scouting/*" element={
                    !user?.superadmin ? (
                      <ProtectedRoute menuKey="module:scouting">
                        <ErrorBoundary moduleName="Scouting"><ScoutingLayout /></ErrorBoundary>
                      </ProtectedRoute>
                    ) : <Navigate to="/" replace />
                  }>
                    <Route index element={<ScoutingDashboard />} />
                    <Route path="phenology" element={
                      <RequireMenu menu="scouting:phenology">
                        <ErrorBoundary moduleName="Phenology"><PhenologyCapture /></ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="config" element={
                      <RequireMenu menu="scouting:pestConfig">
                        <ErrorBoundary moduleName="Pest Configuration"><PestConfigScreen /></ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="field-v2" element={
                      <RequireMenu menu="scouting:fieldModeV2">
                        <ErrorBoundary moduleName="Field Mode (new)"><FieldScoutModePage /></ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="manual-v2" element={
                      <RequireMenu menu="scouting:manualCaptureV2">
                        <ErrorBoundary moduleName="Manual Capture (new)"><FieldScoutDesktopPage /></ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="review" element={
                      <RequireMenu menu="scouting:reviewV2">
                        <ErrorBoundary moduleName="Scouting Review"><ReviewSummaryPage /></ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="review/matrix" element={
                      <RequireMenu menu="scouting:reviewV2">
                        <ErrorBoundary moduleName="Pest Pressure Matrix"><ReviewMatrixPage /></ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                  <Route path="/audit/*" element={
                    !user?.superadmin ? (
                      <RequireMenu menu="auditing:sprayReport">
                        <ErrorBoundary moduleName="Audit">
                          <AuditLayout />
                        </ErrorBoundary>
                      </RequireMenu>
                    ) : <Navigate to="/" replace />
                  }>
                    <Route index element={<AuditLanding />} />
                    <Route path="application-report" element={
                      <RequireMenu menu="auditing:sprayReport">
                        <AuditReportDashboard />
                      </RequireMenu>
                    } />
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                  <Route path="/consumables/*" element={
                    !user?.superadmin ? (
                      <ErrorBoundary moduleName="Consumables">
                        <ConsumablesLayout />
                      </ErrorBoundary>
                    ) : <Navigate to="/" replace />
                  }>
                    <Route index element={<Navigate to="catalog" replace />} />
                    <Route path="catalog" element={
                      <RequireMenu menu="consumables:catalog">
                        <ErrorBoundary moduleName="Consumables / Catalog">
                          <ConsumableCatalogPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="catalog/:id" element={
                      <RequireMenu menu="consumables:catalog">
                        <ErrorBoundary moduleName="Consumables / Catalog Detail">
                          <ConsumableCatalogDetailPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    {/* Back-compat redirect for /consumables/inventory bookmarks. Exact-path match only — /inventory/count is unaffected. */}
                    <Route path="inventory" element={<Navigate to="/consumables/stock-levels" replace />} />
                    <Route path="stock-levels" element={
                      <RequireMenu menu="consumables:inventory">
                        <ErrorBoundary moduleName="Consumables / Stock Levels">
                          <ConsumableStockLevelsPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="stock-movements" element={
                      <RequireMenu menu="consumables:inventory">
                        <ErrorBoundary moduleName="Consumables / Stock Movements">
                          <ConsumableStockMovementsPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="unit-review" element={
                      <RequireMenu menu="consumables:unitReview">
                        <ErrorBoundary moduleName="Consumables / Unit Review">
                          <UnitReviewPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    {/* /inventory/count must precede /procurement so the literal path matches first */}
                    <Route path="inventory/count" element={
                      <RequireMenu menu="consumables:inventory">
                        <ErrorBoundary moduleName="Consumables / Stock Count">
                          <ConsumableStockCountPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    {/* /new must precede /:id so React Router matches the literal segment first. */}
                    <Route path="procurement/new" element={
                      <RequireMenu menu="consumables:procurement">
                        <ErrorBoundary moduleName="Consumables / Procurement">
                          <ConsumableProcurementFormPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="procurement/:id" element={
                      <RequireMenu menu="consumables:procurement">
                        <ErrorBoundary moduleName="Consumables / Procurement Detail">
                          <ConsumableProcurementDetailPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="procurement" element={
                      <RequireMenu menu="consumables:procurement">
                        <ErrorBoundary moduleName="Consumables / Procurement">
                          <ConsumableProcurementListPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                  <Route path="/nutrition/*" element={
                    !user?.superadmin ? (
                      <ErrorBoundary moduleName="Nutrition">
                        <NutritionLayout />
                      </ErrorBoundary>
                    ) : <Navigate to="/" replace />
                  }>
                    <Route index element={
                      <RequireMenu menu="fertilizing:overview">
                        <ErrorBoundary moduleName="Nutrition / Overview">
                          <NutritionDashboardPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    {/* F1 re-homes the programme list under the workspace. Redirect rather than
                        delete so bookmarks keep working. F4 collapses /:id/edit and /:id/review
                        into the single programme editor at foliar/setup/programmes/:id, and
                        /new into the setup tab's create modal (Ruling 7). */}
                    <Route path="programs" element={<Navigate to="/nutrition/foliar/setup" replace />} />
                    <Route path="programs/new" element={<Navigate to="/nutrition/foliar/setup" replace />} />
                    <Route path="programs/:id/review" element={<LegacyProgrammeEditorRedirect />} />
                    <Route path="programs/:id/edit" element={<LegacyProgrammeEditorRedirect />} />
                    <Route path="foliar" element={
                      <ErrorBoundary moduleName="Nutrition / Foliar"><FoliarWorkspaceLayout /></ErrorBoundary>
                    }>
                      <Route index element={
                        <RequireMenu menu="fertilizing:sessions"><FoliarBoardPage /></RequireMenu>
                      } />
                      {/* F3: a genuine tab, unlike foliar/programmes/:id/rounds/:id and
                          foliar/sessions/:id below, which are full-width siblings. */}
                      <Route path="sign-off" element={
                        <RequireMenu menu="fertilizing:signoff"><FoliarSignOffPage /></RequireMenu>
                      } />
                      {/* F1 re-homed the EXISTING programme list here; F4 replaces it with the
                          full Programme setup tab. */}
                      <Route path="setup" element={
                        <RequireMenu menu="fertilizing:programme"><FoliarSetupPage /></RequireMenu>
                      } />
                    </Route>
                    {/* F3: moved OUT of the <Route path="foliar"> layout block. As a child it
                        rendered inside the workspace's Board TabPanel — a second PageHeader, a
                        TabBar above it, and a programme <select> resolved from the tenant's
                        ACTIVE programme rather than this round's. Soil declares every non-tab
                        surface as a sibling for exactly this reason. jsdom cannot see it: the
                        page's suite mounts it on a flat route.

                        No ambiguity with the layout branch: "foliar" has no child matching
                        programmes/:programmeId/rounds/:roundId, so that branch cannot match this
                        URL at all. */}
                    <Route path="foliar/programmes/:programmeId/rounds/:roundId" element={
                      <RequireMenu menu="fertilizing:sessions">
                        <ErrorBoundary moduleName="Nutrition / Foliar Round"><FoliarRoundPage /></ErrorBoundary>
                      </RequireMenu>
                    } />
                    {/* F4: the programme editor. A SIBLING of the foliar layout, not a tab: a
                        focused editing surface with its own SaveBar, like soil/programs/:id/review.
                        No ambiguity with the layout branch — <Route path="foliar"> has exactly
                        three children (index, sign-off, setup) and none of them consumes
                        "setup/programmes/:id", so that branch cannot match this URL at all. */}
                    <Route path="foliar/setup/programmes/:id" element={
                      <RequireMenu menu="fertilizing:programme">
                        <ErrorBoundary moduleName="Nutrition / Foliar Program"><FoliarProgrammeEditorPage /></ErrorBoundary>
                      </RequireMenu>
                    } />
                    {/* F4: round definition editor (spec §4.5). Sibling for the same reason, and
                        a different path depth from the F3 round page above it. */}
                    <Route path="foliar/programmes/:programmeId/rounds/:roundId/edit" element={
                      <RequireMenu menu="fertilizing:programme">
                        <ErrorBoundary moduleName="Nutrition / Foliar Round Editor"><FoliarRoundEditorPage /></ErrorBoundary>
                      </RequireMenu>
                    } />
                    {/* Spec §3.2. A sibling of the workspace, not a tab — a focused full-width
                        surface, like soil/sessions/:id. */}
                    <Route path="foliar/sessions/:id" element={
                      <RequireMenu menu="fertilizing:sessions">
                        <ErrorBoundary moduleName="Nutrition / Session Detail"><NutritionSessionDetailPage /></ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="soil" element={
                      <ErrorBoundary moduleName="Nutrition / Soil"><SoilWorkspaceLayout /></ErrorBoundary>
                    }>
                      <Route index element={
                        <RequireMenu menu="fertilizing:sessions"><SoilBoardPage /></RequireMenu>
                      } />
                      <Route path="sign-off" element={
                        <RequireMenu menu="fertilizing:signoff"><SoilSignOffPage /></RequireMenu>
                      } />
                      <Route path="setup" element={
                        <RequireMenu menu="fertilizing:programme"><SoilProgramsListPage /></RequireMenu>
                      } />
                    </Route>
                    {/* U2: the grouping builder. A sibling of the workspace rather than a tab —
                        it is a focused full-width editing surface, not one of the workspace views. */}
                    <Route path="soil/sessions/:id/grouping" element={
                      <RequireMenu menu="fertilizing:sessions">
                        <ErrorBoundary moduleName="Nutrition / Soil Grouping"><SoilGroupingPage /></ErrorBoundary>
                      </RequireMenu>
                    } />
                    {/* U3: session detail. A sibling of the workspace, like the grouping builder —
                        a focused full-width surface, not one of the workspace's tabs. Declared after
                        the grouping route for readability, but order does not matter here: v6 ranks
                        by specificity, and "soil/sessions/:id/grouping" (4 segments) and
                        "soil/sessions/:id" (3 segments) never match the same URL — there is no
                        splat/wildcard route in this block that could swallow either path. */}
                    <Route path="soil/sessions/:id" element={
                      <RequireMenu menu="fertilizing:sessions">
                        <ErrorBoundary moduleName="Nutrition / Soil Session"><SoilSessionDetailPage /></ErrorBoundary>
                      </RequireMenu>
                    } />
                    {/* U4: CSV import and program review. Siblings of the workspace, like the
                        grouping builder and session detail above — focused full-width surfaces,
                        not workspace tabs. soil/group-sets/:id (Task 6) is NOT added here: it
                        cannot be shadowed by either of these (no shared segment pattern), so
                        adding it in Task 6 does not require touching this block again. */}
                    <Route path="soil/setup/import" element={
                      <RequireMenu menu="fertilizing:programme">
                        <ErrorBoundary moduleName="Nutrition / Soil Program Import"><SoilProgramImportPage /></ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="soil/programs/:id/review" element={
                      <RequireMenu menu="fertilizing:programme">
                        <ErrorBoundary moduleName="Nutrition / Soil Program Review"><SoilProgramReviewPage /></ErrorBoundary>
                      </RequireMenu>
                    } />
                    {/* Task 6: the group-set editor. A sibling of the workspace, like the
                        routes above — its :id is a SET id, not a programme id. */}
                    <Route path="soil/group-sets/:id" element={
                      <RequireMenu menu="fertilizing:programme">
                        <ErrorBoundary moduleName="Nutrition / Soil Group Set"><SoilGroupSetEditorPage /></ErrorBoundary>
                      </RequireMenu>
                    } />
                    {/* U1 re-homed the soil stack under /nutrition/soil. Redirect rather than delete
                        so bookmarks and any missed in-app link keep working. */}
                    <Route path="soil-programs" element={<Navigate to="/nutrition/soil/setup" replace />} />
                    <Route path="sign-off" element={<Navigate to="/nutrition/soil/sign-off" replace />} />
                    <Route path="soil-programs/:id/sessions" element={<LegacySoilSessionsRedirect />} />
                    <Route path="soil-programs/import" element={<Navigate to="/nutrition/soil/setup/import" replace />} />
                    <Route path="soil-programs/:id/review" element={<LegacySoilReviewRedirect />} />
                    <Route path="soil-application-sessions/:id" element={<LegacySoilSessionDetailRedirect />} />
                    <Route path="soil-application-sessions/:id/instruction" element={
                      <RequireMenu menu="fertilizing:sessions">
                        <ErrorBoundary moduleName="Nutrition / Soil Instruction"><SoilInstructionPage /></ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="soil-groups" element={
                      <RequireLinkedOperator>
                        <RequireMenu menu="fertilizing:mysessions">
                          <ErrorBoundary moduleName="Nutrition / My Soil Applications">
                            <SoilMyGroupsPage />
                          </ErrorBoundary>
                        </RequireMenu>
                      </RequireLinkedOperator>
                    } />
                    <Route path="soil-groups/:sessionId/:groupId" element={
                      <RequireLinkedOperator>
                        <RequireMenu menu="fertilizing:mysessions">
                          <ErrorBoundary moduleName="Nutrition / Soil Group Field Capture">
                            <SoilGroupFieldPage />
                          </ErrorBoundary>
                        </RequireMenu>
                      </RequireLinkedOperator>
                    } />
                    <Route path="products" element={
                      <RequireMenu menu="fertilizing:products">
                        <ErrorBoundary moduleName="Nutrition / Products">
                          <NutritionProductsPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    {/* F6 (spec §3.2): the mixed sessions list is retired. It was kept routed and
                        unlinked through F1-F5 because it was the only surface listing legacy soil
                        sessions; those are now reachable only through Back Capture, by design. */}
                    <Route path="sessions" element={<Navigate to="/nutrition/foliar" replace />} />
                    <Route path="sessions/:id" element={<LegacyFoliarSessionDetailRedirect />} />
                    {/* F6: legacy soil session detail retired. Target is the soil WORKSPACE, not
                        /nutrition/soil/sessions/:id — that route reads SoilApplicationSession, a
                        different collection, so the legacy id would 404 on a live-looking page. */}
                    <Route path="soil-sessions/:id" element={<Navigate to="/nutrition/soil" replace />} />
                    <Route path="operator-sessions/:comboId" element={
                      <RequireLinkedOperator>
                        <ErrorBoundary moduleName="Nutrition / Foliar Application Session">
                          <NutritionFoliarFieldPage />
                        </ErrorBoundary>
                      </RequireLinkedOperator>
                    } />
                    {/* F6: the legacy soil operator page is retired. Redirect rather than delete —
                        the authenticated tree has NO catch-all (#450), so a deleted route renders a
                        BLANK screen, and every printed legacy soil sheet's QR points here.
                        Target is soil's OPERATOR worklist: the scanner is an operator, and
                        RequireLinkedOperator already matches. The legacy session will not appear
                        there (different collection) — that is accepted; see the F6 plan, Ruling 7. */}
                    <Route path="soil-field/:id" element={<Navigate to="/nutrition/soil-groups" replace />} />
                    <Route path="audit" element={
                      <RequireMenu menu="fertilizing:audit">
                        <ErrorBoundary moduleName="Nutrition / Audit">
                          <NutritionAuditPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="inventory" element={
                      <RequireMenu menu="fertilizing:inventory">
                        <ErrorBoundary moduleName="Nutrition / Inventory">
                          <NutritionInventoryPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="inventory/movements" element={
                      <RequireMenu menu="fertilizing:inventory">
                        <ErrorBoundary moduleName="Nutrition / Inventory">
                          <NutritionInventoryPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="inventory/orders" element={
                      <RequireMenu menu="fertilizing:inventory">
                        <ErrorBoundary moduleName="Nutrition / Inventory">
                          <NutritionInventoryPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="my-sessions" element={
                      <RequireLinkedOperator>
                        <RequireMenu menu="fertilizing:mysessions">
                          <ErrorBoundary moduleName="Nutrition / My Foliar Applications">
                            <NutritionMySessionsPage />
                          </ErrorBoundary>
                        </RequireMenu>
                      </RequireLinkedOperator>
                    } />
                    {/*
                      #482: Back Capture is retired. Its work now happens
                      inline on the foliar session page's Record action. The
                      list path redirects (house pattern — see the
                      /nutrition/sessions/:id redirect above); the two form
                      paths are deep links nobody bookmarks and are allowed to
                      404.
                    */}
                    <Route path="back-capture" element={<Navigate to="/nutrition/foliar" replace />} />
                    <Route path="data-transfer" element={
                      <RequireMenu menu="fertilizing:csvImport">
                        <ErrorBoundary moduleName="Nutrition / Data Transfer">
                          <DataTransferPanel module="fertilizing" />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                  <Route path="/reconciliation" element={
                    !user?.superadmin ? (
                      <RequireMenu menu="scouting:dataExplorer">
                        <ErrorBoundary moduleName="Reconciliation"><ReconciliationDashboard /></ErrorBoundary>
                      </RequireMenu>
                    ) : <Navigate to="/" replace />
                  } />
                  <Route path="/system/*" element={
                    user?.superadmin ? (
                      <ErrorBoundary moduleName="System Admin">
                        <SystemLayout />
                      </ErrorBoundary>
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }>
                    <Route index element={<Navigate to="/system/tenants" replace />} />
                    <Route path="tenants" element={
                      <RequireMenu menu="system:tenants">
                        <ErrorBoundary moduleName="System Admin / Tenants">
                          <SystemTenantsPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="users" element={
                      <RequireMenu menu="system:users">
                        <ErrorBoundary moduleName="System Admin / Users">
                          <SystemUsersPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="pests" element={
                      <RequireMenu menu="system:pests">
                        <ErrorBoundary moduleName="System Admin / Pests">
                          <SystemPestsPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="crops" element={
                      <RequireMenu menu="system:crops">
                        <ErrorBoundary moduleName="System Admin / Crops">
                          <SystemCropTypesPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="integrity" element={
                      <RequireMenu menu="system:integrity">
                        <ErrorBoundary moduleName="System Admin / Integrity">
                          <SystemIntegrityPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                  {/* Phone check: every signed-in user, superadmin included, no menu gate. */}
                  <Route path="/phone-check" element={
                    <ErrorBoundary moduleName="Phone check"><PhoneCheckPage /></ErrorBoundary>
                  } />
                  <Route path="/data-recovery" element={
                    !user?.superadmin ? (
                      <ErrorBoundary moduleName="Data Recovery"><OrphanRecoveryDashboard /></ErrorBoundary>
                    ) : <Navigate to="/" replace />
                  } />
                  <Route path="/forbidden" element={<div>Forbidden</div>} />
                  <Route path="/settings" element={
                    !user?.superadmin ? (
                      <Navigate to="/settings/tenant" replace />
                    ) : <Navigate to="/" replace />
                  } />
                  <Route path="/settings/me/*" element={
                    !user?.superadmin ? (
                      <RequireMenu menu="settings:user">
                        <ErrorBoundary moduleName="Settings"><Settings /></ErrorBoundary>
                      </RequireMenu>
                    ) : <Navigate to="/" replace />
                  } />
                  <Route path="/settings/tenant/*" element={
                    !user?.superadmin ? (
                      <RequireMenu menu="settings:tenant">
                        <ErrorBoundary moduleName="Settings"><Settings /></ErrorBoundary>
                      </RequireMenu>
                    ) : <Navigate to="/" replace />
                  } />
                  <Route path="/protection/*" element={
                    !user?.superadmin ? (
                      <ErrorBoundary moduleName="Protection">
                        <SprayLayout />
                      </ErrorBoundary>
                    ) : <Navigate to="/" replace />
                  }>
                    <Route index element={<SprayDashboard />} />
                    <Route path="my-sessions" element={
                      <RequireLinkedOperator>
                        <OperatorSessionList />
                      </RequireLinkedOperator>
                    } />
                    <Route path="operator-sessions/:comboId" element={
                      <RequireLinkedOperator>
                        <ErrorBoundary moduleName="Operator Combo Work Flow">
                          <OperatorComboWorkFlow />
                        </ErrorBoundary>
                      </RequireLinkedOperator>
                    } />
                    <Route path="sessions/:id/complete" element={<SpraySessionCompleteLanding />} />
                    <Route path="sessions" element={<RequireMenu menu="spraying:sessions"><SpraySessionsPage /></RequireMenu>} />
                    <Route path="back-capture" element={<RequireMenu menu="spraying:backCapture"><BackCaptureListPage /></RequireMenu>} />
                    <Route path="back-capture/:comboId" element={<RequireMenu menu="spraying:backCapture"><BackCaptureFormPage /></RequireMenu>} />
                    <Route path="programs" element={<RequireMenu menu="spraying:programs"><SprayProgramsPage /></RequireMenu>} />
                    <Route path="products" element={<RequireMenu menu="spraying:products"><SprayProductsPage /></RequireMenu>} />
                    <Route path="products/:id" element={<RequireMenu menu="spraying:products"><SprayProductDetailPage /></RequireMenu>} />
                    {/* Back-compat redirect for /protection/inventory bookmarks. */}
                    <Route path="inventory" element={<Navigate to="/protection/stock-levels" replace />} />
                    <Route path="stock-levels" element={
                      <RequireMenu menu="spraying:inventory">
                        <ErrorBoundary moduleName="Protection / Stock Levels">
                          <SprayStockLevelsPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="stock-movements" element={
                      <RequireMenu menu="spraying:inventory">
                        <ErrorBoundary moduleName="Protection / Stock Movements">
                          <SprayStockMovementsPage />
                        </ErrorBoundary>
                      </RequireMenu>
                    } />
                    <Route path="machines" element={<RequireMenu menu="spraying:machines"><SprayMachinesPage /></RequireMenu>} />
                    <Route path="operators" element={<RequireMenu menu="spraying:operators"><SprayOperatorsPage /></RequireMenu>} />
                    <Route path="import" element={<RequireMenu menu="spraying:csvImport"><DataTransferPanel module="spraying" /></RequireMenu>} />
                    <Route path="weather-history" element={
                      <ProtectedRoute menuKey="spraying:weatherHistory">
                        <WeatherHistoryDashboard />
                      </ProtectedRoute>
                    } />
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </AppShell>
            </MustChangePasswordGate>
          } />
        </Routes>

        <UpdateToast show={showUpdateToast} onUpdate={updateApp} onDismiss={handleDismissToast} />

        <ToastContainer position="bottom-end" className="p-3">
          <Toast show={showStaleWarning} onClose={() => setShowStaleWarning(false)} delay={10000} autohide bg="warning">
            <Toast.Header>
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <strong className="me-auto">Offline Data Warning</strong>
            </Toast.Header>
            <Toast.Body>
              Your cached orchard data is older than 7 days. Please connect to the internet to ensure you have the latest boundaries.
            </Toast.Body>
          </Toast>
        </ToastContainer>
      </div>
      )}
    </Router>
  );
}

function App() {
  return (
    <ModuleNavProvider>
      <BreadcrumbProvider>
        <AppInner />
      </BreadcrumbProvider>
    </ModuleNavProvider>
  );
}

export default App;
